import _, {isEqual, isUndefined} from 'lodash';
import {Reducer} from 'redux';
import {Mutable} from 'utility-types';

import {PredicateOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {isShallowEqual} from '../../../libs/shared/helpers/src/utilities/objectHelpers';
import {DataGroupDeletableModel, DataGroupModel} from '../../models/api/dataGroupModel';
import {
  DeletableResourceTable,
  emptyDeletableResourceTable,
  emptyLazyResourceTable,
  emptyResourceTable,
  LazyResourceTable,
  ResourceMap,
  ResourceTable,
} from '../../store/appTypes';
import {ActionBase} from '../reduxHelpers';

/*
 * Helpers.
 */

/** Resource with updatedAt. */
interface UpdatableResource {
  updatedAt: number;
}

/** Key of DataGroupModel that contains resource map with updatedAt. */
type DataGroupUpdatableKey = {
  [K in keyof DataGroupModel]-?: DataGroupModel[K] extends ReadonlyArray<UpdatableResource> | undefined
    ? K
    : never;
}[keyof DataGroupModel];

/** Items defined in the deletable data group. */
type DataGroupDeletableKey = keyof DataGroupDeletableModel;

/** Resource with updatedAt in DataGroupModel. */
type DataGroupUpdatableResource<K extends DataGroupUpdatableKey> = Required<DataGroupModel>[K][0];

/** Resource in DataGroupDeletableModel. */
type DataGroupDeletableResource<K extends DataGroupDeletableKey> = DataGroupDeletableModel[K][0];

/** Merges an existing resource and an updated resource. */
type ResourceMerger<T> = (existingResource: T | undefined, updatedResource: T) => T;

/** Transforms a resource into it's deleted version. */
type ResourceDeletedTransformer<T, U> = (existingResource: T) => U;

// TODO: Deprecate once FetchCalendarEventVersionsForViewCompletedAction is replaced by DataUpdatedAction.
interface DataUpdatedActionLike extends ActionBase {
  payload: {
    result: DataGroupModel;
    shouldIgnoreUpdatedAt?: boolean;
  };
}

export function defaultResourceMerger<T>(existing: T | undefined, updated: T): T {
  // Return updated if nothing to update.
  if (isUndefined(existing)) {
    return updated;
  }

  // If existing and updated are equal, avoid an identity change.
  if (isEqual(existing, updated)) {
    return existing;
  }

  return updated;
}

export function createResourcesUpdatedReducer<K extends DataGroupUpdatableKey>(
  dataGroupKey: K,
  getResourceId: (resource: DataGroupUpdatableResource<K>) => number | string,
  mergeResource: ResourceMerger<DataGroupUpdatableResource<K>> = defaultResourceMerger,
): Reducer<ResourceMap<DataGroupUpdatableResource<K>>, DataUpdatedActionLike> {
  return (state = {}, action) =>
    resourcesUpdatedReducer(
      getResourceId,
      action.payload.shouldIgnoreUpdatedAt ?? false,
      mergeResource,
      state,
      action.payload.result[dataGroupKey],
    );
}

export function createResourceTableUpdatedReducer<
  K extends DataGroupUpdatableKey,
  TId extends number | string,
>(
  dataGroupKey: K,
  getResourceId: (resource: DataGroupUpdatableResource<K>) => TId,
  mergeResource: ResourceMerger<DataGroupUpdatableResource<K>> = defaultResourceMerger,
): Reducer<ResourceTable<TId, DataGroupUpdatableResource<K>>, DataUpdatedActionLike> {
  return createGenericResourceTableUpdatedReducer(
    dataGroupKey,
    getResourceId,
    emptyResourceTable,
    mergeResource,
  );
}

export function createDeletableResourceTableUpdatedReducer<
  K extends DataGroupUpdatableKey & DataGroupDeletableKey,
  TId extends number | string,
>(
  dataGroupKey: K,
  getResourceId: (resource: DataGroupUpdatableResource<K>) => TId,
  buildDeletedResource: ResourceDeletedTransformer<
    DataGroupUpdatableResource<K>,
    DataGroupDeletableResource<K>
  >,
  mergeResource: ResourceMerger<DataGroupUpdatableResource<K>> = defaultResourceMerger,
): Reducer<
  DeletableResourceTable<TId, DataGroupUpdatableResource<K>, DataGroupDeletableResource<K>>,
  DataUpdatedActionLike
> {
  return createGenericDeletableResourceTableUpdatedReducer(
    dataGroupKey,
    getResourceId,
    emptyDeletableResourceTable,
    buildDeletedResource,
    mergeResource,
  );
}

export function createLazyResourceTableUpdatedReducer<
  K extends DataGroupUpdatableKey,
  TId extends number | string,
>(
  dataGroupKey: K,
  getResourceId: (resource: DataGroupUpdatableResource<K>) => TId,
  mergeResource: ResourceMerger<DataGroupUpdatableResource<K>> = defaultResourceMerger,
): Reducer<LazyResourceTable<TId, DataGroupUpdatableResource<K>>, DataUpdatedActionLike> {
  return createGenericResourceTableUpdatedReducer(
    dataGroupKey,
    getResourceId,
    emptyLazyResourceTable,
    mergeResource,
  );
}

function createGenericResourceTableUpdatedReducer<
  K extends DataGroupUpdatableKey,
  TId extends number | string,
  TResourceTable extends ResourceTable<TId, DataGroupUpdatableResource<K>>,
>(
  dataGroupKey: K,
  getResourceId: (resource: DataGroupUpdatableResource<K>) => TId,
  defaultState: TResourceTable,
  mergeResource: ResourceMerger<DataGroupUpdatableResource<K>> = defaultResourceMerger,
): Reducer<TResourceTable, DataUpdatedActionLike> {
  return (state = defaultState, action) => {
    // Use shouldIgnoreUpdatedAt as a signal to ignore all conflicts (update timestamps AND tombstones).
    // TODO: Deprecate shouldIgnoreUpdatedAt once we deprecated task queues?
    const shouldIgnoreConflicts = action.payload.shouldIgnoreUpdatedAt ?? false;
    const updates = action.payload.result[dataGroupKey];

    const {byId, deletedIds} = state;
    const newById = resourcesUpdatedReducer(
      getResourceId,
      shouldIgnoreConflicts,
      mergeResource,
      byId,
      updates,
      shouldIgnoreConflicts ? () => false : (id) => deletedIds.contains(id),
    );
    const newDeletedIds =
      shouldIgnoreConflicts && updates ? deletedIds.subtract(updates.map(getResourceId)) : deletedIds;

    if (byId === newById && deletedIds === newDeletedIds) {
      return state;
    }

    return {
      ...state,
      byId: newById,
      deletedIds: newDeletedIds,
    };
  };
}

function createGenericDeletableResourceTableUpdatedReducer<
  K extends DataGroupUpdatableKey & DataGroupDeletableKey,
  TId extends number | string,
  TResourceTable extends DeletableResourceTable<
    TId,
    DataGroupUpdatableResource<K>,
    DataGroupDeletableResource<K>
  >,
>(
  dataGroupKey: K,
  getResourceId: (resource: DataGroupUpdatableResource<K>) => TId,
  defaultState: TResourceTable,
  buildDeletedResource: ResourceDeletedTransformer<
    DataGroupUpdatableResource<K>,
    DataGroupDeletableResource<K>
  >,
  mergeResource: ResourceMerger<DataGroupUpdatableResource<K>> = defaultResourceMerger,
): Reducer<TResourceTable, DataUpdatedActionLike> {
  return (state = defaultState, action) => {
    // Use shouldIgnoreUpdatedAt as a signal to ignore all conflicts (update timestamps AND tombstones).
    // TODO: Deprecate shouldIgnoreUpdatedAt once we deprecated task queues?
    const shouldIgnoreConflicts = action.payload.shouldIgnoreUpdatedAt ?? false;
    const updates = action.payload.result[dataGroupKey];

    const {byId, deletedIds, deletedById} = state;
    const newById = resourcesUpdatedReducer(
      getResourceId,
      shouldIgnoreConflicts,
      mergeResource,
      byId,
      updates,
      shouldIgnoreConflicts ? () => false : (id) => deletedIds.contains(id),
    );
    const newDeletedIds =
      shouldIgnoreConflicts && updates ? deletedIds.subtract(updates.map(getResourceId)) : deletedIds;

    const newDeletedById =
      shouldIgnoreConflicts && updates
        ? _([...newDeletedIds])
            .map((id) => newById[id])
            .compact()
            .map((t) => buildDeletedResource(t))
            .keyBy((t) => t.id)
            .value()
        : deletedById;

    if (
      isShallowEqual(byId, newById) &&
      deletedIds.equals(newDeletedIds) &&
      isShallowEqual(deletedById, newDeletedById)
    ) {
      return state;
    }

    return {
      ...state,
      byId: newById,
      deletedIds: newDeletedIds,
      deletedById: newDeletedById,
    };
  };
}

function resourcesUpdatedReducer<TId extends number | string, T extends UpdatableResource>(
  getResourceId: (resource: T) => TId,
  shouldIgnoreUpdatedAt: boolean,
  mergeResource: ResourceMerger<T>,
  state: ResourceMap<T>,
  updates: ReadonlyArray<T> | undefined,
  isDeletedId: PredicateOf<TId> = () => false,
): ResourceMap<T> {
  if (!updates || updates.length === 0) {
    return state;
  }

  // Either the new state, or undefined if there are no state changes.
  let newState: Mutable<ResourceMap<T>> | undefined;

  // Optimization: iterate the updates once instead of _().groupBy/keyBy/sortBy/maxBy.
  updates.forEach((updatedResource) => {
    const resourceId = getResourceId(updatedResource);
    if (isDeletedId(resourceId)) {
      return;
    }

    const existingResource = (newState || state)[resourceId];
    if (!shouldUpdateResourceForUpdatedAt(existingResource, updatedResource, shouldIgnoreUpdatedAt)) {
      return;
    }

    const newResource = mergeResource(existingResource, updatedResource);
    if (newResource === existingResource) {
      return;
    }

    // Optimization: create a copy of the state once on the first state change.
    newState = newState || {...state};
    newState[resourceId] = newResource;
  });

  return newState || state;
}

export function shouldUpdateResourceForUpdatedAt<T extends UpdatableResource>(
  existingResource: T | undefined,
  updatedResource: T,
  shouldIgnoreUpdatedAt = false,
): boolean {
  if (!existingResource) {
    return true;
  }

  return shouldIgnoreUpdatedAt || existingResource.updatedAt <= updatedResource.updatedAt;
}

/**
 * Creates an updater that replaces a resource as long as the updated resource is newer.
 *
 * This lets you do an optimistic update (with optimisticUpdateAsyncAction) using the resource updatedAt as an
 * optimistic lock for conflict resolution.
 *
 * Note: it's better to avoid conflicts altogether and dynamically update the resource instead!
 */
export function createUpdatableResourceUpdater<T extends UpdatableResource>(
  updatedResource: T,
): (resource: T) => T {
  return (existingResource: T) =>
    shouldUpdateResourceForUpdatedAt(existingResource, updatedResource) ? updatedResource : existingResource;
}
